import { AdequacyAggregateType, ExportFormatType, ExportGapFilterType, ExportProviderAvailabilityType, ExportType, HSDFormatType, NetworkProviderFilterType, ProviderDropdownTypes } from "@app/exports/enumerations";
import { Features } from "@app/feature-flags";
import { Roles } from "@app/user";
import { ServicingProvidersReport } from "./views/ServicingProviderReport";
import { CountySummaryReport } from "./views/CountySummaryReport";
import { StateSummaryReport } from "./views/StateSummaryReport";
import { SpecialtySummaryReport } from "./views/SpecialtySummaryReport";
import { AllSpecialtiesReport } from "./views/AllSpecialtiesReport";
import { toast } from "react-toastify";
import { SpecialtySummaryByCountyReport } from "./views/SpecialtySummaryByCountyReport";
import { ClosestProviderReport } from "./views/ClosestProviderReport";
import { ZipSummariesReport } from "./views/ZipSummariesReport";
import { ReportType } from "./interfaces/reportTypeEnum";
import { JSX } from "react";
import { ProviderNetworkType } from "@app/projects/enumerators";
import ExportService from "@app/exports/exportService";
import { ExportModels } from "./interfaces/exportModels";
import { ExportColumn } from "./interfaces/exportColumns";

const exportService = new ExportService();

interface FormatsReturnType {
  key: string;
  value: string;
}

export const getDefaultReportName = (prefix: string): string => {
  // this returns an array formatted like ['02', '03', '2025, 10:48:54 AM']
  /* eslint-disable-next-line sort-keys */
  const date = (new Date()).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).split("/");
  // ['2025', '10:48:54 AM']
  const yearAndTime = date[2].split(", ");
  // ['10', '48', '54']
  const time = yearAndTime[1].split(" ")[0].split(":");
  // 20250203_104854
  const dateString = `${yearAndTime[0]}${date[1]}${date[0]}_${time[0]}${time[1]}${time[2]}`;

  return `${prefix}_${dateString}`;
}

export const getDefaultReportNamePrefixByReportType = (reportType: ReportType, isCompareMode?: boolean, providerSource?: ProviderNetworkType | ProviderDropdownTypes): string => {
  switch (reportType) {
    case ReportType.AdequacyServicingProvider:
      if (isCompareMode)
        return "Changed Providers";

      switch (providerSource) {
        case ProviderNetworkType.Market:
          return "Market";
        case ProviderNetworkType.Prospect:
          return "Prospect";
        case ProviderNetworkType.NetNew:
          return "Net New";
        case ProviderDropdownTypes.NextClosestProviders:
          return "Next Closest Provider";
        case ProviderNetworkType.Network:
        default:
          return "Network";
      }
    case ReportType.StateSummary:
      return isCompareMode ? "State Comparison" : "State";
    case ReportType.CountySummary:
      return isCompareMode ? "County Comparison" : "County";
    case ReportType.AdequacySpecialtySummary:
      return isCompareMode ? "Specialty Comparison" : "Specialty";
    case ReportType.AdequacyAggregate:
      return isCompareMode ? "Specialties Comparison" : "Specialties";
    case ReportType.SpecialtySummaryByCounty:
      return isCompareMode ? "Summary Comparison" : "Summary";
    case ReportType.AdequacyNextClosestProvider:
      // no compare mode option
      return "Closest Providers to Members";
    case ReportType.ZipSummary:
      return isCompareMode ? "Zip Summaries Comparison" : "Zip Summaries";
  }
}

export const ReportTypeNamesMap = new Map<ReportType, string>([
  [ReportType.AdequacySpecialtySummary, "Specialty Summary"],
  [ReportType.AdequacySpecialtySummaryComparison, "Specialty Summmary"],
  [ReportType.CountySummary, "County Summary"],
  [ReportType.CountySummaryComparison, "County Summary"],
  [ReportType.StateSummary, "State Summary"],
  [ReportType.StateSummaryComparison, "State Summary"],
  [ReportType.AdequacyAggregate, "All Specialties"],
  [ReportType.SpecialtySummaryByCounty, "Specialty Summary By County"],
  [ReportType.SpecialtySummaryByCountyComparison, "Specialty Summary By County"],
  [ReportType.AdequacyServicingProvider, "Providers"],
  [ReportType.ZipSummary, "Zip Summaries"],
  [ReportType.ZipSummaryComparison, "Zip Summary"],
  [ReportType.AdequacyNextClosestProvider, "Closest Provider to Members"],
]);

export const reportTypeRouteMap = new Map<ReportType, JSX.Element>([
  [ReportType.AdequacyServicingProvider, <ServicingProvidersReport key="servicing-providers" />],
  [ReportType.CountySummary, <CountySummaryReport key="county-aggregate" />],
  [ReportType.StateSummary, <StateSummaryReport key="state-aggregate" />],
  [ReportType.AdequacySpecialtySummary, <SpecialtySummaryReport key="specialty-aggregate" />],
  [ReportType.AdequacyAggregate, <AllSpecialtiesReport key="all-specialties" />],
  [ReportType.SpecialtySummaryByCounty, <SpecialtySummaryByCountyReport key="ssbcr" />],
  [ReportType.AdequacyNextClosestProvider, <ClosestProviderReport key="closest-provider" />],
  [ReportType.ZipSummary, <ZipSummariesReport key="zip-summaries" />],
]);

export const sharedUsersAggregateTypeMap = new Map<ReportType, AdequacyAggregateType>([
  [ReportType.AdequacyServicingProvider, undefined],
  [ReportType.CountySummary, AdequacyAggregateType.RegionSet],
  [ReportType.StateSummary, AdequacyAggregateType.RegionSet],
  [ReportType.AdequacySpecialtySummary, AdequacyAggregateType.SpecialtyGroup],
  [ReportType.AdequacyNextClosestProvider, undefined],
  [ReportType.ZipSummary, undefined],
  [ReportType.AdequacyAggregate, undefined],
  [ReportType.SpecialtySummaryByCounty, undefined],
]);

export const exportTypeToSharedUsersExportTypeMap = new Map<ReportType, string>([
  [ReportType.AdequacyAggregate, "AdequacyAggregate"],
  [ReportType.AdequacyNextClosestProvider, "ClosestProvidersToMembers"],
  [ReportType.CountySummary, "AdequacyBulkAggregateReport"],
  [ReportType.AdequacyServicingProvider, "AdequacyServicingProvider"],
  [ReportType.AdequacySpecialtySummary, "AdequacyAggregate"],
  [ReportType.SpecialtySummaryByCounty, "AdequacySummary"],
  [ReportType.StateSummary, "AdequacyBulkAggregateReport"],
  [ReportType.ZipSummary, "ZipSummary"],
]);

export const getFileFormats = (tooLarge?: boolean, reportType?: ReportType): FormatsReturnType[] => {
  const fileFormats: Array<{ key: ExportFormatType, value: string }> = [
    { key: ExportFormatType.CSV, value: "Comma-Delimited Text File (.csv)" },
    { key: ExportFormatType.Tab, value: "Tab-Delimited Text File (.txt)" },
    { key: ExportFormatType.Pipe, value: "Bar-Delimited Text File (.txt)" },
  ];

  if (!tooLarge && reportType !== ReportType.AdequacyNextClosestProvider && reportType !== ReportType.ZipSummary) {
    fileFormats.push({ key: ExportFormatType.Excel, value: "Microsoft Excel Workbook (.xlsx)" });
  }

  return fileFormats;
}

export const getDefaultFileFormat = (tooLarge?: boolean, reportType?: ReportType): ExportFormatType => {
  const reportTypesThatDoNotDefaultToExcel = [ReportType.AdequacyNextClosestProvider, ReportType.ZipSummary, ReportType.AdequacyServicingProvider];
  if (!tooLarge && !reportTypesThatDoNotDefaultToExcel.includes(reportType)) {
    return ExportFormatType.Excel;
  }

  return ExportFormatType.CSV;
}

export const getProvidersReportFormats = (userRoles: string[], featureFlags: Set<string>, providerSource: ProviderNetworkType): FormatsReturnType[] => {
  const hsdFormatFilters = [
    { key: HSDFormatType.Default, value: "Default Format" },
    { key: HSDFormatType.MA, value: "MA HSD Format" },
    { key: HSDFormatType.MMP, value: "MMP HSD Format" },
  ];

  if (userRoles.includes(Roles.CCIIO_PPF_REPORT_USER)) {
    hsdFormatFilters.push({ key: HSDFormatType.CCIIO_PPF_REPORT, value: "CCIIO PPF Report" });
  }

  if (userRoles.includes(Roles.DISTINCT_SERVICING_USER)
    && featureFlags.has(Features.DISTINCT_SERVICING_PROVIDER_REPORT)
    && (providerSource === ProviderNetworkType.Network
      || providerSource === ProviderNetworkType.Prospect
      || providerSource === ProviderNetworkType.Market
    )) {
    hsdFormatFilters.push({ key: HSDFormatType.DISTINCT_SERVICING_PROVIDER_REPORT, value: "Distinct Servicing Providers" })
  }

  return hsdFormatFilters;
}

export const getServiceAreaGapTypes = (userRoles: string[]): FormatsReturnType[] => {
  const filters = [
    { key: ExportGapFilterType.All, value: `All Areas` },
    { key: ExportGapFilterType.Gaps, value: `Areas with Gaps` },
    { key: ExportGapFilterType.FillableBySingleProvider, value: `Areas with Gaps Fillable by Single Provider` },
    { key: ExportGapFilterType.Exceptions, value: `Areas with Exceptions` },
  ];
  if (userRoles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
    filters.push(
      { key: ExportGapFilterType.FillableGaps, value: `Areas with Fillable Gaps` }
    )
  }
  return filters;
}

export const getSpecialtyGapTypes = (userRoles: string[], featureFlags: Set<string>): FormatsReturnType[] => {
  const filters = [
    { key: ExportGapFilterType.All, value: "All Specialties" },
    { key: ExportGapFilterType.Gaps, value: "Specialties with Gaps" },
    { key: ExportGapFilterType.FillableBySingleProvider, value: "Specialties with Gaps Fillable by Single Provider" },
    { key: ExportGapFilterType.Exceptions, value: "Specialties with Exceptions" },
  ];
  if (userRoles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER)) {
    filters.push(
      { key: ExportGapFilterType.FillableGaps, value: "Specialties with Fillable Gaps" }
    )
  }
  if (featureFlags.has(Features.GAP_FILL_REPORT_UPDATES)) {
    filters.push(
      { key: ExportGapFilterType.GapResolutionStatus, value: "Specialties with Gap Resolution Status" }
    )
  }
  return filters;
}

export const getProviderFilterOptions = (isNetwork?: boolean): FormatsReturnType[] => {
  if (isNetwork) {
    return [
      { key: NetworkProviderFilterType.Available, value: "All Providers" },
      { key: NetworkProviderFilterType.Unterminated, value: "Network Minus Terminated Providers" },
      { key: NetworkProviderFilterType.Terminated, value: "Terminated Providers" },
    ];
  }

  return [
    { key: ExportProviderAvailabilityType.All, value: "All Providers" },
    { key: ExportProviderAvailabilityType.Available, value: "Available Providers" },
    { key: ExportProviderAvailabilityType.Excluded, value: "Excluded Providers" },
    { key: ExportProviderAvailabilityType.Targeted, value: "Targeted Providers" },
  ];
}

export const handleToastSuccess = (filename: string): void => {
  toast(`${filename} has been queued for generation, check email or Reports page for status.`, {
    closeOnClick: true,
    containerId: "z-index-9999",
    draggable: false,
    type: "info",
  });
}

export const handleToastError = (filename: string): void => {
  toast(`${filename} has failed, please try again or contact your system administrator.`, {
    closeOnClick: true,
    containerId: "z-index-9999",
    draggable: false,
    type: "error",
  });
}

export const exportTypeToReportTypeMap = new Map<ExportType, ReportType>([
  [ExportType.ServicingProviders, ReportType.AdequacyServicingProvider],
  [ExportType.AllSpecialties, ReportType.AdequacyAggregate],
  [ExportType.ZipSummary, ReportType.ZipSummary],
  [ExportType.ClosestProvider, ReportType.AdequacyNextClosestProvider],
  [ExportType.SpecialtyAggregate, ReportType.AdequacySpecialtySummary],
  [ExportType.StateAggregate, ReportType.StateSummary],
  [ExportType.CountyAggregate, ReportType.CountySummary],
  [ExportType.SpecialtySummaryByCounty, ReportType.SpecialtySummaryByCounty],
]);


export const reportTypeToColumnsEndpointMap = new Map<ReportType, (model: ExportModels) => Promise<ExportColumn[]>>([
  [ReportType.AdequacyServicingProvider, exportService.getAdequacyServicingProvidersColumns],
  [ReportType.AdequacyAggregate, exportService.getAdequacyAggregatesColumns],
  [ReportType.ZipSummary, exportService.getZipSummariesColumns],
  [ReportType.AdequacyNextClosestProvider, exportService.getClosestProviderColumns],
  [ReportType.AdequacySpecialtySummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.StateSummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.CountySummary, exportService.getAdequacyAggregatesColumns],
  [ReportType.SpecialtySummaryByCounty, exportService.getAdequacySpecialtySummaryColumns],
]);
